/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import Store from '../store/store';
import Types from '../constants/actionTypes';
import {
  updateTaskList,
  removeFromTaskList,
  removeTaskFromBroadcast,
  addTaskFromBroadcast,
  updateTaskFromBroadcast
} from '../actions/taskActions';
import {
  addWorkPackageFromBroadcast,
  updateWorkPackageFromBroadcast,
  deleteWorkPackageFromBroadcast,
  fetchAllWorkPackages,
  updateRanksFromBroadcast
} from '../actions/workPackageActions';
import { updateCriteriaStore } from '../actions/criteriaActions';
import { updateTeamStore } from '../actions/teamActions';
import { updateAttachmentsStore } from '../actions/attachmentActions';
import {
  updateBurndownData,
  updateCadencesStore,
  deleteCadencesStore
} from '../actions/cadenceActions';
import {
  updateTargetBurnupData,
  updateTargetStore
} from '../actions/targetActions';
import { updateUserStore } from '../actions/userActions';
import { updateOrganizationStore } from '../actions/organizationActions';
import { handleProductBroadcast } from '../actions/productActions';
import { resultInTeamScope } from './workPackageHelpers';
import { updateMultipleResults } from '../actions/commonDispatches';
import { getCadenceId, getWorkPackage } from './reduxHelpers';
import Consumer from '../channels/consumer';

const dataFormatter = new Jsona();

const getClientData = (response) => {
  const clientReceived = response.client || '';
  const browserIdReceived = response.browser_id || '';
  const currentUser = Store.getState().userReducer.user;
  const currentBrowserId = Store.getState().defaultOptionsReducer.browserId;

  return {
    clientReceived,
    browserIdReceived,
    currentUser,
    currentBrowserId
  };
};

const isSameBrowserAndClient = (response) => {
  const {
    clientReceived,
    browserIdReceived,
    currentUser,
    currentBrowserId
  } = getClientData(response);

  return (
    currentUser.client === clientReceived &&
    browserIdReceived === currentBrowserId
  );
};

const parseAndDeserialize = (data) =>
  dataFormatter.deserialize(JSON.parse(data));

const subscribeTasksUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.tasks = window.App.subscriptions.create(
      { channel: 'TasksChannel' },
      {
        received: (response) => {
          if (isSameBrowserAndClient(response) && response.type !== 'delete')
            return;
          const taskList = dataFormatter.deserialize(
            response?.tasks || response?.updated
          );
          if (taskList && taskList.length > 0) {
            if (response.delete) {
              Store.dispatch(removeFromTaskList(response.delete));
            } else if (response.type === 'update') {
              taskList.forEach((t) =>
                Store.dispatch(updateTaskFromBroadcast(t))
              );
            } else {
              Store.dispatch(updateTaskList(taskList));
            }
          }
          if (response && response.updated) {
            // eslint-disable-next-line no-useless-catch
            try {
              const task = parseAndDeserialize(response.updated);
              switch (response.type) {
                case 'create':
                  Store.dispatch(addTaskFromBroadcast(task));
                  break;
                case 'delete':
                  Store.dispatch(removeTaskFromBroadcast(response.updated)); // updated is the task ID
                  break;
                default:
                  Store.dispatch(updateTaskFromBroadcast(task));
              }
            } catch (err) {
              throw err;
            }
          }
          // }
        }
      }
    );
  }
};

const parseProductResponse = (response) => {
  if (isSameBrowserAndClient(response)) return;

  let product;
  if (typeof response.product !== 'number') {
    product = parseAndDeserialize(response.product);
  } else {
    // this gets triggered when we destroy a product - the destroyed product's
    // id is what the client recieves in this case
    product = response.product;
  }

  // team_id gets passed as a 3rd argument when adding or removing teams from product
  Store.dispatch(
    handleProductBroadcast(product, response.type, response.team_id)
  );
};

const subscribeProductUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.products = window.App.subscriptions.create(
      {
        channel: 'ProductChannel'
      },
      { received: (response) => parseProductResponse(response) }
    );
  }
};

const handleWorkPackageRefresh = (response) => {
  // eslint-disable-next-line camelcase
  const { team_ids, product_ids } = response;
  const { selectedSpace } = Store.getState().workPackageReducer;

  let affectedProduct;
  let affectedTeam;
  if (selectedSpace.type === 'product') {
    affectedProduct = product_ids.includes(selectedSpace.id);

    if (affectedProduct) return Store.dispatch(fetchAllWorkPackages());

    const selectedSpaceTeamIds = selectedSpace.teams.map((team) => team.id);

    affectedTeam = team_ids.some((id) => selectedSpaceTeamIds.includes(id));

    if (affectedTeam) return Store.dispatch(fetchAllWorkPackages());
  } else if (selectedSpace.type === 'team') {
    affectedTeam = team_ids.includes(selectedSpace.id);

    if (affectedTeam) return Store.dispatch(fetchAllWorkPackages());

    const selectesSpaceProductIds = selectedSpace.products.map(
      (prod) => prod.id
    );

    affectedProduct = product_ids.some((id) =>
      selectesSpaceProductIds.includes(id)
    );
    if (affectedProduct) return Store.dispatch(fetchAllWorkPackages());
  }
};

const updateWorkPackages = (response = {}) => {
  if (isSameBrowserAndClient(response) && response.type !== 'status_change')
    return;

  if (response && response.refresh) {
    return handleWorkPackageRefresh(response);
  }
  if (response && response.updated) {
    // eslint-disable-next-line no-useless-catch
    try {
      let workPackage;
      switch (response.type) {
        case 'create':
          workPackage = parseAndDeserialize(response.updated);
          Store.dispatch(
            addWorkPackageFromBroadcast(workPackage, response.ranks_and_ids)
          );
          break;
        case 'delete':
          Store.dispatch(deleteWorkPackageFromBroadcast(response.updated));
          // response.updated for the delete case refers to the id of the workpackage
          // that just got permanently deleted
          break;
        case 'rank_update':
          Store.dispatch(updateRanksFromBroadcast(response.ranks_and_ids));
          break;
        default:
          workPackage = parseAndDeserialize(response.updated);

          if (
            getWorkPackage(workPackage.id) && // if in store
            !!workPackage.team_id && // if team_id is not null
            !resultInTeamScope(workPackage) // if result's team is not in team reducer
          ) {
            Store.dispatch({
              type: Types.DELETE_WORK_PACKAGE,
              workPackage,
              cadenceId: getCadenceId()
            });
            break;
          }

          /*
              dispatch with deletion_strategy due to special handling of
              soft deleted workpackages and their children. value is empty string
              if no soft deletion took place
            */
          Store.dispatch(
            updateWorkPackageFromBroadcast(
              workPackage,
              response.ranks_and_ids,
              response.deletion_strategy
            )
          );
      }
    } catch (err) {
      throw err;
    }
  }
};

const handleCriteriaUpdate = (response) => {
  if (isSameBrowserAndClient(response)) return;
  Store.dispatch(updateCriteriaStore(response));
};

const handleTeamUpdates = (response) => {
  if (isSameBrowserAndClient(response)) return;
  Store.dispatch(updateTeamStore(response.team, response.action));
};

const handleTargetUpdates = (response) => {
  if (isSameBrowserAndClient(response)) return;

  const target =
    response.type !== 'delete'
      ? dataFormatter.deserialize(response.target)
      : response.target;

  Store.dispatch(updateTargetStore(target, response.type));
};

const handleUserUpdates = (response) => {
  if (isSameBrowserAndClient(response)) return;

  const user =
    response.type !== 'delete'
      ? parseAndDeserialize(response.user)
      : response.user;

  Store.dispatch(updateUserStore(user, response.type));
};

const subscribeWorkPackagesUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.workPackages = window.App.subscriptions.create(
      {
        channel: 'WorkPackagesChannel',
        id: Store.getState().cadenceReducer.cadence.id
      },
      { received: (response) => updateWorkPackages(response.work_packages) }
    );
  }
};

const subscribeMultipleWorkPackagesUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.workPackages = window.App.subscriptions.create(
      { channel: 'MultipleWorkPackagesChannel' },
      {
        received: (response) => {
          const updatedWorkPackages = parseAndDeserialize(
            response.work_packages.updated_work_packages
          );

          if (isSameBrowserAndClient(response.work_packages)) return;

          if (updatedWorkPackages.length >= 1) {
            // if unscoped workpackages are received, they are filetered out in the reducer
            updateMultipleResults(Store.dispatch, updatedWorkPackages);
          }
        }
      }
    );
  }
};

const subscribeOrganizationUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.organizations = window.App.subscriptions.create(
      {
        channel: 'OrganizationChannel'
      },
      {
        received: (response) => {
          const organization = parseAndDeserialize(response.organization);
          Store.dispatch(
            updateOrganizationStore(
              organization,
              response.action,
              response.current_user_id
            )
          );
        }
      }
    );
  }
};

const subscribeCriteriaUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.criteria = window.App.subscriptions.create(
      { channel: 'CriteriaChannel' },
      {
        received: (response) => {
          handleCriteriaUpdate(response);
        }
      }
    );
  }
};

const subscribeTeamUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.teams = window.App.subscriptions.create(
      { channel: 'TeamChannel' },
      {
        received: (response) => {
          handleTeamUpdates(response);
        }
      }
    );
  }
};

const subscribeAttachmentsUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.attachments = window.App.subscriptions.create(
      { channel: 'AttachmentsChannel' },
      {
        received: (response) => {
          const data = parseAndDeserialize(response);
          Store.dispatch(updateAttachmentsStore(data));
        }
      }
    );
  }
};

const subscribeBurndownUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.burndown = window.App.subscriptions.create(
      { channel: 'BurndownChannel' },
      {
        received: (response) => {
          const data = response?.graph_data || {};
          const cadenceId = response.cadence_id;
          Store.dispatch(updateBurndownData(cadenceId, data));
        }
      }
    );
  }
};

const subscribeTargetUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.teams = window.App.subscriptions.create(
      { channel: 'TargetChannel' },
      {
        received: (response) => {
          handleTargetUpdates(response);
        }
      }
    );
  }
};

const subscribeUserUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.teams = window.App.subscriptions.create(
      { channel: 'UserChannel' },
      {
        received: (response) => {
          handleUserUpdates(response);
        }
      }
    );
  }
};

const subscribeTargetBurnupUpdates = () => {
  if (typeof App !== 'undefined') {
    window.App.target_burnup = window.App.subscriptions.create(
      { channel: 'TargetBurnupChannel' },
      {
        received: (response) => {
          const data =
            response && response.graph_data ? response.graph_data : {};
          Store.dispatch(updateTargetBurnupData(data));
        }
      }
    );
  }
};

const subscribeCadencesUpdates = () => {
  if (typeof App !== 'undefined') {
    if (Store.getState().cadenceReducer.cadence?.id) {
      window.App.cadences = window.App.subscriptions.create(
        {
          channel: 'CadencesChannel',
          id: Store.getState().cadenceReducer.cadence?.id
        },
        {
          received: (response) => {
            const data = parseAndDeserialize(response.cadence);
            if (response.type === 'delete') {
              Store.dispatch(deleteCadencesStore(response.cadence)); // will be the cadence ID
            } else {
              Store.dispatch(updateCadencesStore(data));
            }
          }
        }
      );
    }
  }
};

const subscribeHeartbeat = () => {
  if (typeof App !== 'undefined') {
    // Save this subscription object in window.App.heartbeat in case we need to unsubscribe/resubscribe
    window.App.heartbeat.subscription = window.App.subscriptions.create(
      { channel: 'HeartbeatChannel' },
      {
        received: (response) => {
          window.App.heartbeat.receive();
        }
      }
    );
  }
};

const subscribe = (callback) => {
  subscribeWorkPackagesUpdates();
  subscribeMultipleWorkPackagesUpdates();
  subscribeTasksUpdates();
  subscribeCriteriaUpdates();
  subscribeTeamUpdates();
  subscribeOrganizationUpdates();
  subscribeAttachmentsUpdates();
  subscribeBurndownUpdates();
  subscribeCadencesUpdates();
  subscribeUserUpdates();
  subscribeTargetUpdates();
  subscribeTargetBurnupUpdates();
  subscribeProductUpdates();
  subscribeHeartbeat();
  callback();
};

export default subscribe;
